// components/EmailTemplateEditor.js
// Platform-owner only section — edit email notification templates.
// Embedded in OwnerDashboard.js, gated behind isPlatformOwner.

import React, { useState, useEffect } from 'react';

const token = () => localStorage.getItem('token');

const TEMPLATE_LABELS = {
  lead_submitted:     'New Lead Submitted',
  lead_approved:      'Lead Approved',
  lead_rejected:      'Lead Rejected',
  lead_converted:     'Lead Converted to Deal',
  deal_stage_changed: 'Deal Stage Changed',
};

const RECIPIENT_LABELS = {
  lead_submitted:     '→ Broker owner(s)',
  lead_approved:      '→ Introducer',
  lead_rejected:      '→ Introducer',
  lead_converted:     '→ Introducer',
  deal_stage_changed: '→ Introducer',
};

const s = {
  card:      { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' },
  cardHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer', userSelect: 'none' },
  cardBody:  { padding: '0 20px 20px', borderTop: '1px solid #F7FAFC' },
  label:     { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 5, marginTop: 14 },
  input:     { width: '100%', border: '1px solid #CBD5E0', borderRadius: 7, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  textarea:  { width: '100%', border: '1px solid #CBD5E0', borderRadius: 7, padding: '9px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 },
  varPill:   { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#EDF2F7', color: '#4a5568', marginRight: 5, marginBottom: 4, fontFamily: 'monospace' },
  saveBtn:   { background: '#10b981', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 22px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  resetBtn:  { background: '#fff', border: '1px solid #CBD5E0', borderRadius: 7, padding: '9px 16px', fontSize: 13, cursor: 'pointer', color: '#718096', marginLeft: 8 },
  successMsg:{ fontSize: 13, color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, padding: '6px 12px', marginLeft: 10 },
  errorMsg:  { fontSize: 13, color: '#c53030', background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 6, padding: '6px 12px', marginLeft: 10 },
};

function TemplateCard({ template, onSaved }) {
  const [open, setOpen]       = useState(false);
  const [form, setForm]       = useState({
    subject:    template.subject    || '',
    body_intro: template.body_intro || '',
    body_extra: template.body_extra || '',
    cta_label:  template.cta_label  || '',
  });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [isError, setIsError] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`/api/owner/email-templates/${template.key}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Save failed');
      setMsg('Saved'); setIsError(false);
      onSaved(template.key, form);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.message); setIsError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this template to default content? Your edits will be lost.')) return;
    setSaving(true);
    try {
      await fetch(`/api/owner/email-templates/${template.key}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      setMsg('Reset to default'); setIsError(false);
      onSaved(template.key, null);
      setTimeout(() => setMsg(''), 3000);
    } catch {
      setMsg('Reset failed'); setIsError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.card}>
      <div style={s.cardHead} onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 16 }}>{open ? '▾' : '▸'}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>{TEMPLATE_LABELS[template.key]}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{RECIPIENT_LABELS[template.key]}</div>
          </div>
        </div>
        {template.updated_at && (
          <span style={{ fontSize: 11, color: '#a0aec0' }}>
            Edited {new Date(template.updated_at).toLocaleDateString('en-GB')}
          </span>
        )}
      </div>

      {open && (
        <div style={s.cardBody}>
          {/* Available variables */}
          <div style={{ marginTop: 14, marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#718096', marginRight: 8 }}>Available variables:</span>
            {(template.variables || []).map(v => (
              <span key={v} style={s.varPill}>{v}</span>
            ))}
          </div>

          <label style={s.label}>Subject line</label>
          <input
            name="subject" value={form.subject} onChange={handleChange}
            placeholder="e.g. New lead from {{introducerName}}: {{clientName}}"
            style={s.input}
          />

          <label style={s.label}>Opening paragraph</label>
          <textarea
            name="body_intro" value={form.body_intro} onChange={handleChange}
            placeholder="Main message body…"
            style={s.textarea} rows={3}
          />

          <label style={s.label}>Additional paragraph <span style={{ fontWeight: 400, color: '#a0aec0' }}>(optional)</span></label>
          <textarea
            name="body_extra" value={form.body_extra} onChange={handleChange}
            placeholder="Secondary message or instructions…"
            style={s.textarea} rows={2}
          />

          <label style={s.label}>Button label</label>
          <input
            name="cta_label" value={form.cta_label} onChange={handleChange}
            placeholder="e.g. View Your Dashboard →"
            style={{ ...s.input, maxWidth: 280 }}
          />

          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            <button onClick={handleSave} disabled={saving} style={s.saveBtn}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button onClick={handleReset} disabled={saving} style={s.resetBtn}>
              Reset to Default
            </button>
            {msg && (
              <span style={isError ? s.errorMsg : s.successMsg}>
                {isError ? '⚠️ ' : '✓ '}{msg}
              </span>
            )}
          </div>

          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#a0aec0' }}>
            HTML is supported in body fields. Variables are replaced with live data when emails are sent.
          </p>
        </div>
      )}
    </div>
  );
}

export default function EmailTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => {
    fetch('/api/owner/email-templates', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => setError('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (key, newValues) => {
    setTemplates(ts => ts.map(t => t.key === key ? { ...t, ...(newValues || {}), updated_at: new Date().toISOString() } : t));
  };

  if (loading) return <div style={{ padding: 20, color: '#718096', fontSize: 14 }}>Loading email templates…</div>;

  return (
    <section style={{ marginTop: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#0e1b2c' }}>Email Notifications</h2>
        <p style={{ margin: 0, fontSize: 14, color: '#718096' }}>
          Customise the content of automated emails sent to brokers and introducers. Use <code style={{ background: '#EDF2F7', padding: '1px 5px', borderRadius: 3, fontSize: 12 }}>{'{{variable}}'}</code> placeholders for dynamic content.
        </p>
      </div>

      {error && <div style={{ color: '#c53030', marginBottom: 16 }}>⚠️ {error}</div>}

      {templates.map(t => (
        <TemplateCard key={t.key} template={t} onSaved={handleSaved} />
      ))}
    </section>
  );
}
